import { Link } from "react-router-dom";
import { LEGAL_UPDATED, OPERATOR_NAME } from "../../../shared/legal";
import { CheckIcon, CrossIcon } from "../../lib/ui";
import { LegalPage } from "./LegalPage";
import { Clause, Label, PrivacyMail } from "./parts";

const CONTENTS: [string, string][] = [
  ["c1", "About these terms"],
  ["c2", "What ConfirmPay does"],
  ["c3", "Who can use it"],
  ["c4", "Your account"],
  ["c5", "Forwarding bank emails"],
  ["c6", "Payment confirmation"],
  ["c7", "Paying through a link"],
  ["c8", "Using it properly"],
  ["c9", "Your public link"],
  ["c10", "Free, changes and shutting down"],
  ["c11", "Your content and our IP"],
  ["c12", "Suspension and ending"],
  ["c13", "No warranties"],
  ["c14", "Limits on our responsibility"],
  ["c15", "Your responsibility to us"],
  ["c16", "Changes to these terms"],
  ["c17", "The legal bits"],
];

export default function Terms() {
  return (
    <LegalPage tab="terms" title="Terms of Service">
      <section className="wrap" style={{ paddingTop: 48, paddingBottom: 56 }}>
        <div className="lg-eyebrow">Terms of Service</div>
        <h1 className="display" style={{ marginTop: 18, maxWidth: 980, fontSize: "min(76px, 11vw)" }}>
          The deal, in plain words.
        </h1>
        <p className="lead" style={{ marginTop: 26, maxWidth: 700 }}>
          ConfirmPay is free. In return we ask you to use it honestly and to check your own bank account. These terms are the agreement between you and us, and Singapore law governs them.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 30 }}>
          <span className="lg-pill">Last updated {LEGAL_UPDATED}</span>
          <span className="lg-pill">Free to use</span>
          <span className="lg-pill">Governed by Singapore law</span>
        </div>
      </section>

      <section className="wrap" style={{ paddingBottom: 72 }}>
        <div className="lg-eyebrow" style={{ marginBottom: 14 }}>The deal in 60 seconds</div>
        <div className="lg-stripes">
          <div className="lg-stripe"><h3>It’s free.</h3><p>No fees today. If that ever changes, you’ll hear first and can leave.</p></div>
          <div className="lg-stripe"><h3>We never hold your money.</h3><p>PayNow goes bank to bank. We aren’t a bank or a payment account.</p></div>
          <div className="lg-stripe"><h3>Check your bank account.</h3><p>“Paid” on ConfirmPay is a convenience. Your bank balance is the truth.</p></div>
          <div className="lg-stripe"><h3>Use it honestly.</h3><p>No scams, impersonation or spam, and no one else’s payments through your link.</p></div>
          <div className="lg-stripe"><h3>Leave any time.</h3><p>Close your account and ask for your data to be deleted.</p></div>
        </div>
      </section>

      <section className="band">
        <div className="wrap" style={{ paddingTop: 80, paddingBottom: 80 }}>
          <div className="lg-bnd">
            <div>
              <div className="lg-eyebrow" style={{ color: "var(--band-mute)" }}>The one thing to remember</div>
              <h2 className="h2" style={{ marginTop: 16, fontSize: "min(48px, 9vw)" }}>Confirmation helps. Your bank account decides.</h2>
              <p style={{ margin: "22px 0 0", fontSize: 18, lineHeight: 1.55, color: "var(--band-mute)", maxWidth: 480 }}>
                ConfirmPay marks a request paid when it recognises a forwarded bank email. Emails can be late, missing or misread. Before you hand over goods, tickets or a receipt, look at your bank account.
              </p>
            </div>
            <div>
              <div className="lg-pm">
                <h4><span style={{ color: "var(--sage)", display: "inline-flex" }}><CheckIcon size={22} /></span>“Paid” means</h4>
                <p>ConfirmPay recognised a bank email that matched this request.</p>
              </div>
              <div className="lg-pm">
                <h4><span style={{ color: "#f0a396", display: "inline-flex" }}><CrossIcon size={22} /></span>It doesn’t mean the money is in your account</h4>
                <p>Check your bank before you release anything.</p>
              </div>
              <div className="lg-pm">
                <h4><span style={{ color: "#f0a396", display: "inline-flex" }}><CrossIcon size={22} /></span>It doesn’t mean the email was genuine</h4>
                <p>Emails can be forged, and formats change.</p>
              </div>
              <div className="lg-pm">
                <h4><span style={{ color: "#f0a396", display: "inline-flex" }}><CrossIcon size={22} /></span>It doesn’t mean the amount was right</h4>
                <p>The payer’s bank confirmation is their proof of payment.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="wrap" style={{ paddingTop: 72, paddingBottom: 56 }} aria-label="Contents">
        <div className="lg-eyebrow" style={{ marginBottom: 14 }}>The full terms</div>
        <ol className="lg-toc">
          {CONTENTS.map(([id, label], i) => (
            <li key={id}>
              <a href={`#${id}`}>
                <span>{String(i + 1).padStart(2, "0")}</span>
                {label}
              </a>
            </li>
          ))}
        </ol>
      </section>

      <div className="wrap" style={{ paddingBottom: 8 }}>
        <Clause n="01" id="c1" title="About these terms" short="Using ConfirmPay means agreeing to these. If you sign up for an organisation, you agree for it." first>
          <p>
            These Terms of Service (the <b>Terms</b>) are an agreement between {OPERATOR_NAME}, an individual in Singapore who runs ConfirmPay (<b>we</b>, <b>us</b>), and you: the organisation or business that signs up, the person acting for it, and anyone who uses a ConfirmPay payment link or page.
          </p>
          <p>
            By creating an account, forwarding bank emails or using a ConfirmPay link, you agree to these Terms and to our <Link to="/privacy">Privacy Policy</Link>. If you accept for an organisation, you confirm you are authorised to bind it. If you don’t agree, please don’t use ConfirmPay.
          </p>
          <p>Agreeing electronically is valid under Singapore’s Electronic Transactions Act 2010.</p>
        </Clause>

        <Clause n="02" id="c2" title="What ConfirmPay does, and doesn’t" short="A tool for asking for PayNow payments and spotting when they arrive. Not a bank, not a wallet.">
          <ol>
            <li>Organisations create payment requests, links and PayNow QR codes.</li>
            <li>A payer scans the QR or follows the link and pays from their own banking app, directly into the organisation’s PayNow-registered bank account.</li>
            <li>If the organisation forwards its bank’s payment notification emails to its private ConfirmPay address, ConfirmPay reads the amount, reference and payer name from them, marks the matching request paid and emails the payer a receipt.</li>
            <li>ConfirmPay <b>never holds, receives, moves or settles money,</b> doesn’t operate an account for anyone, and is not a bank or anyone’s agent. A payment is between the payer, their bank and the organisation’s bank.</li>
            <li>ConfirmPay is independent. It is not affiliated with or endorsed by the Monetary Authority of Singapore, the Association of Banks in Singapore, PayNow or any bank. Those names belong to their owners.</li>
          </ol>
        </Clause>

        <Clause n="03" id="c3" title="Who can use it" short="Singapore organisations and small businesses, run by adults who can act for them.">
          <p>To use ConfirmPay for an organisation you must:</p>
          <ol>
            <li>be at least 18 and able to enter a binding agreement;</li>
            <li>act for a nonprofit or business in Singapore, or be a sole proprietor; and</li>
            <li>have a Singapore bank account that can receive PayNow payments, on the mobile number or UEN you give us.</li>
          </ol>
          <p>
            We may check new organisations before switching their payment links and QR codes on, and we may decline or pause any sign-up. Until an organisation is approved, its links and QR codes stay off.
          </p>
        </Clause>

        <Clause n="04" id="c4" title="Your account" short="You sign in with a code emailed to your contact address. Guard that inbox.">
          <ol>
            <li>You sign in with a code emailed to your contact email, so keep that inbox secure. You are responsible for what happens under your account.</li>
            <li>Keep your private forwarding address secret. Anyone who has it can send emails into your account. Rotate it from your dashboard if it leaks.</li>
            <li>Give us accurate details and keep them up to date.</li>
            <li>The PayNow mobile number or UEN you connect must be your organisation’s own, and you must be entitled to receive payments to it. We may ask you to prove a mobile number by sending a WhatsApp message from it.</li>
            <li>Tell us promptly at <PrivacyMail /> if you think someone else has got into your account. “Sign out everywhere” in your dashboard ends every other session at once.</li>
          </ol>
        </Clause>

        <Clause n="05" id="c5" title="Forwarding bank emails" short="You choose what to forward. Keep it to your bank’s payment notices, and stop whenever you like.">
          <ol>
            <li>By setting up forwarding you authorise us to receive and process those emails as our Privacy Policy describes.</li>
            <li>Forward only your bank’s payment notification emails. Set your filter to match your bank’s sender addresses and nothing else.</li>
            <li>You are responsible for your own compliance with the law, including the PDPA, for the payer details in your requests and in those emails. For that data we act on your behalf as your data intermediary.</li>
            <li>You can stop forwarding at any time by deleting the filter in your inbox. You don’t need to ask us.</li>
          </ol>
        </Clause>

        <Clause n="06" id="c6" title="Payment confirmation is a convenience" short="“Paid” means we recognised an email. It doesn’t mean the money is in your account.">
          <ol>
            <li>Confirmation depends on emails from banks, which we don’t control. They can arrive late or not at all, change format, be misread, or, rarely, be forged. We match carefully, but we can’t promise accuracy or timing.</li>
            <li>A request marked paid, or a receipt we send, is not proof that money reached your account. <b>Check your own bank account before you hand over goods, services, tickets or receipts.</b></li>
            <li>You can mark a request paid by hand. You are responsible for doing so correctly.</li>
            <li>We aren’t responsible for payments sent to the wrong PayNow number or UEN, or for amounts that differ from the request. A payer’s own bank confirmation is their proof of payment.</li>
          </ol>
        </Clause>

        <Clause n="07" id="c7" title="Paying through a ConfirmPay link" short="You pay your bank-to-bank. Questions about what you paid for go to the organisation.">
          <p>If you are paying an organisation through a ConfirmPay link:</p>
          <ol>
            <li>You pay from your own banking app, straight to the organisation. ConfirmPay isn’t a party to your purchase or donation.</li>
            <li>Refunds, disputes and tax-deduction receipts are between you and the organisation. The receipt we email is a payment acknowledgement, not an official tax receipt.</li>
            <li>Check that the payee name your banking app shows is the organisation you meant to pay before you confirm.</li>
          </ol>
        </Clause>

        <Clause n="08" id="c8" title="Using it properly" short="Be honest, be decent, don’t break it." tight>
          <p>You must not use ConfirmPay to:</p>
          <ol>
            <li>break the law, defraud or mislead anyone, launder money, or run payments for other people through your account;</li>
            <li>pretend to be another person, organisation, bank or ConfirmPay, or choose a link name meant to mislead;</li>
            <li>put unlawful, abusive or misleading content in descriptions or names;</li>
            <li>spam payers, flood a link, probe or attack our systems, scrape the service or get around rate limits;</li>
            <li>forward emails that aren’t your own bank’s payment notices; or</li>
            <li>enter someone else’s personal data without the right to.</li>
          </ol>
          <p>
            To enforce this and to protect people, we may remove content, rename or switch off a link, or suspend an account. We may report suspected unlawful activity to the authorities.
          </p>
        </Clause>
        <div className="lg-vis">
          <div className="lg-fnf">
            <div>
              <h4><span style={{ color: "var(--sage-ink)", display: "inline-flex" }}><CheckIcon size={22} /></span>Fine</h4>
              <p>Asking members to pay a fee, a donation or a ticket price.</p>
              <p>Putting your link on posters, WhatsApp groups and your website.</p>
              <p>Switching the link off when a campaign ends.</p>
              <p>Marking a payment paid by hand after you’ve seen it in your bank.</p>
            </div>
            <div>
              <h4><span style={{ color: "var(--red-ink)", display: "inline-flex" }}><CrossIcon size={22} /></span>Not fine</h4>
              <p>Collecting money for another person or group under your PayNow number.</p>
              <p>A link name that copies a bank, a known charity or ConfirmPay.</p>
              <p>Forwarding mail that isn’t a bank payment notice.</p>
              <p>Hammering a link to “test” it, or hunting for gaps in our security.</p>
            </div>
          </div>
        </div>

        <Clause n="09" id="c9" title="Your public link" short="Your link is public. You can switch it off in one click." first>
          <ol>
            <li>Your link, confirmpay.link/your-name, is public. Anyone who has it can start a payment request for your organisation. We use rate limits to cut spam.</li>
            <li>You can switch it off at any time from your dashboard. Requests you create yourself keep working.</li>
            <li>We may refuse or reclaim a link name that impersonates someone or is misleading.</li>
            <li>If you change your link name, the old one stays reserved for you so that posters and shared links keep working.</li>
          </ol>
        </Clause>

        <Clause n="10" id="c10" title="Free, changes and shutting down" short="Free, as-is, no promise of uptime. If we ever stop, you get notice and your records.">
          <ol>
            <li>ConfirmPay is free. We make no promise of availability, uptime or support.</li>
            <li>We may change, limit, pause or discontinue any part of the service, including pausing new sign-ups.</li>
            <li>If we discontinue the service, we will give at least 30 days’ notice by email to your contact address and a way to export your records (CSV), unless the law or safety needs us to act sooner.</li>
            <li>We charge nothing today. If we ever introduce fees, they would only apply to you if you agree after we tell you.</li>
          </ol>
        </Clause>

        <Clause n="11" id="c11" title="Your content and our IP" short="Your content stays yours. ConfirmPay’s name, look and code stay ours.">
          <ol>
            <li>You keep your rights in the names, descriptions and other content you submit. You give us a limited licence to host, display and process it to run the service for you.</li>
            <li>ConfirmPay’s name, logos, site design and software belong to us or our licensors. Don’t copy them or suggest that we endorse you.</li>
            <li>If you send us feedback, we may use it without owing you anything.</li>
          </ol>
        </Clause>

        <Clause n="12" id="c12" title="Suspension and ending" short="Leave whenever you like. We can pause an account that puts others at risk.">
          <ol>
            <li>You can stop using ConfirmPay at any time and ask us to delete your data with the <Link to="/delete-data">delete form</Link>.</li>
            <li>We may suspend or end your access straight away if you break these Terms, if the law requires it, or to prevent harm or fraud. Otherwise we’ll give reasonable notice.</li>
            <li>When access ends, your payment links and QR codes stop working in ConfirmPay, and we handle your data as the Privacy Policy says. Money already paid is unaffected, because it went straight to your bank.</li>
            <li>Clauses meant to last, such as 13 to 15 and 17, survive.</li>
          </ol>
        </Clause>

        <Clause n="13" id="c13" title="No warranties" short="ConfirmPay is provided as it is.">
          <p>
            To the extent the law allows, ConfirmPay is provided “as is” and “as available”, without warranties of any kind, express or implied. That includes the accuracy or timing of payment confirmation, uninterrupted or error-free operation, and fitness for a particular purpose.
          </p>
          <p>Nothing on ConfirmPay is financial, tax, accounting or legal advice.</p>
        </Clause>

        <Clause n="14" id="c14" title="Limits on our responsibility" short="We’re responsible for what the law says we must be. We’re not responsible for your lost sales or for your bank.">
          <ol>
            <li>Nothing in these Terms excludes or limits liability that the law does not allow us to exclude or limit, including liability for death or personal injury caused by negligence, for fraud, or any right you have as a consumer that cannot be excluded.</li>
            <li>Subject to (a), and to the extent the law allows, we are not liable for indirect or consequential loss, or for loss of profit, revenue, goodwill, data or opportunity, or for loss caused by banks, PayNow, email providers or other third parties, or by your not checking payments against your bank account.</li>
            <li>Subject to (a) and (b), our total liability to you for all claims connected with ConfirmPay is limited to S$50.</li>
          </ol>
          <p>ConfirmPay is free, and these limits apply to the extent permitted by the Unfair Contract Terms Act 1977 and other applicable law.</p>
        </Clause>

        <Clause n="15" id="c15" title="Your responsibility to us" short="If your misuse leads to a claim against us, you cover it.">
          <p>
            You will compensate us for losses, claims and reasonable costs that arise from your breach of these Terms, your unlawful use of ConfirmPay, or content you submit. We will tell you promptly about any claim and, where it’s reasonable, let you handle the defence.
          </p>
        </Clause>

        <Clause n="16" id="c16" title="Changes to these terms" short="14 days’ notice for changes that matter.">
          <p>
            We may change these Terms. For changes that matter, we’ll email your contact address and update the date at the top at least 14 days before they take effect. If you keep using ConfirmPay after that, you accept the new Terms. If you don’t, you can close your account first.
          </p>
        </Clause>

        <Clause n="17" id="c17" title="The legal bits" short="Singapore law and Singapore courts.">
          <ol>
            <li><b>Governing law.</b> These Terms are governed by the laws of Singapore.</li>
            <li><b>Disputes.</b> Please contact us first and give us 30 days to sort it out. If we can’t, the courts of Singapore have exclusive jurisdiction.</li>
            <li><b>Whole agreement.</b> These Terms and the Privacy Policy are the whole agreement between us about ConfirmPay.</li>
            <li><b>If a clause fails.</b> If part of these Terms can’t be enforced, the rest still applies.</li>
            <li><b>No waiver.</b> Not enforcing a right straight away doesn’t give it up.</li>
            <li><b>Transfers.</b> You may not transfer these Terms. We may transfer them to a successor of the service, and will tell you if we do.</li>
            <li><b>Other people.</b> A person who is not a party to these Terms has no right under the Contracts (Rights of Third Parties) Act 2001 to enforce any of them.</li>
            <li><b>Notices.</b> We’ll send notices to your contact email. You can write to us at the email below.</li>
          </ol>
          <div className="lg-contact">
            <div><Label>Operator</Label>{OPERATOR_NAME}</div>
            <div><Label>Email</Label><PrivacyMail /></div>
          </div>
        </Clause>
      </div>
    </LegalPage>
  );
}
